/**
 * @fileoverview Month View Component for Room-Centric Planner
 * @module features/coordinator/planner/MonthView
 * @description Traditional calendar grid showing monthly room occupancy overview
 * @created 10-10-2025 20:57:00
 * @author MCHD Room Scheduler Team
 */

import React, { useMemo, useCallback } from 'react';
import {
  Paper,
  Text,
  Group,
  Stack,
  Badge,
  Grid,
  Card,
  Tooltip,
  Loader,
  Center,
} from '@mantine/core';
import {
  getMonthDates,
  formatCalendarHeader,
  isDateToday,
  isDatePast,
  getOccupancyColor,
  enrichCellStatusAssignment,
} from './utils/calendarHelpers';

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

export interface Assignment {
  id: string;
  title: string;
  roomId: string;
  userId: string;
  sessionTemplateId: string;
  date: string;
  status: 'CONFIRMED' | 'CANCELLED';
  published: boolean;
  conflictCode?: string;
  user?: {
    id: string;
    name: string;
    role: string;
  };
  room?: {
    id: string;
    number: string;
    name: string;
  };
  sessionTime?: {
    id: string;
    name: string;
    startTime: string;
    endTime: string;
  };
  users?: {
    id: string;
    name: string;
  };
}

export interface CellStatus {
  roomId: string;
  sessionTemplateId: string;
  date: string;
  status: 'empty' | 'occupied' | 'blocked' | 'inactive' | 'conflict' | 'holiday';
  assignment?: Assignment;
}

export interface MonthViewProps {
  /** Current viewing date */
  currentDate: Date;
  
  /** Total number of available slots (rooms × session times) */
  totalSlotsPerDay: number;
  
  /** Cell statuses data from API */
  cellStatuses: CellStatus[];
  
  /** Full assignments array with nested data */
  assignments?: Assignment[];
  
  /** Loading state */
  isLoading?: boolean;
  
  /** Callback when a date is clicked */
  onDateClick?: ((date: Date) => void) | undefined;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get day statistics and assignments
 * ENHANCED: 15-10-2025 13:15:00 - Now returns actual assignments for display
 */
function getDayStats(cellStatuses: CellStatus[], assignments: Assignment[], date: Date, totalSlots: number) {
  const dateString = date.toISOString().split('T')[0];
  const dateCells = cellStatuses.filter((cs) => cs.date === dateString);
  
  // Enrich cell statuses with full assignment data
  const enrichedDateCells = dateCells.map(cs => 
    enrichCellStatusAssignment(cs, assignments)
  );
  
  const confirmed = enrichedDateCells.filter(
    (cs) => cs.assignment?.status === 'CONFIRMED'
  ).length;
  
  const conflicts = enrichedDateCells.filter((cs) => cs.status === 'conflict').length;
  
  const blocked = enrichedDateCells.filter((cs) => cs.status === 'blocked').length;
  
  const holiday = enrichedDateCells.some((cs) => cs.status === 'holiday');
  
  const assignmentsForUtil = enrichedDateCells
    .filter((cs) => cs.assignment && cs.assignment.status === 'CONFIRMED')
    .map((cs) => cs.assignment as any);
  
  const utilization = totalSlots > 0 
    ? Math.round((assignmentsForUtil.length / totalSlots) * 100)
    : 0;

  return {
    confirmed,
    conflicts,
    blocked,
    holiday,
    utilization,
    hasActivity: confirmed > 0 || conflicts > 0 || blocked > 0,
    assignments: assignmentsForUtil, // ADDED: Return actual assignments for display
    cellsWithConflicts: dateCells.filter((cs) => cs.status === 'conflict'), // ADDED: Conflict details
  };
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

/**
 * Day Cell Component
 */
interface DayCellProps {
  date: Date;
  currentMonth: Date;
  stats: ReturnType<typeof getDayStats>;
  onDateClick?: ((date: Date) => void) | undefined; // FIXED: 10-10-2025 21:40:00 - Now takes date parameter
}

const DayCell: React.FC<DayCellProps> = ({ date, currentMonth, stats, onDateClick }) => {
  const isToday = isDateToday(date);
  const isPast = isDatePast(date);
  const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
  const utilizationColor = getOccupancyColor(stats.utilization);

  // FIXED: 10-10-2025 22:35:00 - Add debugging to prevent render-time clicks
  const handleClick = useCallback((e: React.MouseEvent) => {
    // DEBUG: 10-10-2025 22:35:00 - Prevent any programmatic clicks during render
    if (!e.isTrusted) {
      console.warn('🚫 [DayCell] Blocked programmatic click during render:', {
        date: date.toISOString(),
        isTrusted: e.isTrusted,
        type: e.type
      });
      return;
    }

    console.log('✅ [DayCell] User click detected:', {
      date: date.toISOString(),
      timestamp: Date.now()
    });

    e.stopPropagation();
    if (onDateClick) {
      onDateClick(date); // Pass date to parent callback
    }
  }, [onDateClick, date]); // Memoize with date dependency

  return (
    <Paper
      p="xs"
      radius="sm"
      withBorder
      style={{
        minHeight: 100,
        cursor: onDateClick ? 'pointer' : 'default',
        backgroundColor: stats.holiday
          ? '#faf5ff'
          : isToday
          ? '#eff6ff'
          : isPast
          ? '#f9fafb'
          : 'white',
        opacity: isCurrentMonth ? 1 : 0.5,
        borderColor: isToday ? '#3b82f6' : undefined,
        borderWidth: isToday ? 2 : 1,
      }}
      onClick={onDateClick ? handleClick : undefined}
    >
      <Stack gap="xs" style={{ height: '100%' }}>
        {/* Date number */}
        <Group justify="space-between" align="flex-start">
          <Text
            size="sm"
            fw={isToday ? 700 : 600}
            c={isToday ? 'blue' : isCurrentMonth ? 'dark' : 'dimmed'}
          >
            {date.getDate()}
          </Text>
          
          {isToday && (
            <Badge size="xs" color="blue" variant="filled">
              Today
            </Badge>
          )}
        </Group>

        {/* Indicators */}
        <Stack gap={4} style={{ flex: 1 }}>
          {stats.holiday && (
            <Badge size="xs" color="purple" variant="light" fullWidth>
              🎉 Holiday
            </Badge>
          )}
          
          {stats.hasActivity && !stats.holiday && (
            <>
              {/* ENHANCED: 15-10-2025 13:15:00 - Display actual assignment details */}
              {stats.assignments && stats.assignments.length > 0 && (
                <Stack gap={2}>
                  {stats.assignments.slice(0, 2).map((assignment: any) => (
                    <Tooltip 
                      key={assignment.id} 
                      label={`${assignment.title || 'Assignment'} - ${assignment.users?.name || assignment.user?.name || 'Unknown Doctor'}`}
                      position="top"
                      withArrow
                    >
                      <Badge 
                        size="xs" 
                        color="blue" 
                        variant="light"
                        style={{ 
                          cursor: 'pointer',
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          maxWidth: '100%'
                        }}
                      >
                        👤 {(assignment.users?.name || assignment.user?.name || 'Unknown')?.split(' ').slice(0, 2).join(' ')}
                      </Badge>
                    </Tooltip>
                  ))}
                  {stats.assignments.length > 2 && (
                    <Tooltip label={`${stats.assignments.length - 2} more assignments`}>
                      <Badge size="xs" color="gray" variant="light">
                        +{stats.assignments.length - 2} more
                      </Badge>
                    </Tooltip>
                  )}
                </Stack>
              )}

              {/* Utilization bar */}
              <Tooltip label={`${stats.utilization}% utilization (${stats.confirmed} assignments)`}>
                <div>
                  <Paper
                    radius="xs"
                    style={{
                      height: 3,
                      backgroundColor: '#e5e7eb',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        width: `${stats.utilization}%`,
                        backgroundColor: `var(--mantine-color-${utilizationColor}-6)`,
                        transition: 'width 0.3s ease',
                      }}
                    />
                  </Paper>
                </div>
              </Tooltip>

              {/* Conflict/Blocked indicators */}
              {(stats.conflicts > 0 || stats.blocked > 0) && (
                <Group gap={2} justify="center">
                  {stats.conflicts > 0 && (
                    <Tooltip label={`${stats.conflicts} conflicts detected`}>
                      <Badge size="xs" color="orange" variant="light">
                        ⚠️ {stats.conflicts}
                      </Badge>
                    </Tooltip>
                  )}
                  
                  {stats.blocked > 0 && (
                    <Tooltip label={`${stats.blocked} slots blocked`}>
                      <Badge size="xs" color="red" variant="light">
                        🚫 {stats.blocked}
                      </Badge>
                    </Tooltip>
                  )}
                </Group>
              )}
            </>
          )}
          
          {!stats.hasActivity && !stats.holiday && !isPast && (
            <Text size="xs" c="dimmed" ta="center" mt="auto">
              Available
            </Text>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/**
 * Month View Component
 * Displays calendar grid with room occupancy indicators for each day
 * 
 * @created 10-10-2025 20:57:00
 */
export const MonthView: React.FC<MonthViewProps> = ({
  currentDate,
  totalSlotsPerDay,
  cellStatuses,
  assignments = [],
  isLoading = false,
  onDateClick,
}) => {
  // Get all dates for the month (including padding days)
  const monthDates = useMemo(() => getMonthDates(currentDate), [currentDate]);

  // Group dates into weeks
  const weeks = useMemo(() => {
    const result: Date[][] = [];
    for (let i = 0; i < monthDates.length; i += 7) {
      result.push(monthDates.slice(i, i + 7));
    }
    return result;
  }, [monthDates]);

  if (isLoading) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <Stack gap="md">
      {/* Month header */}
      <Paper p="md" withBorder>
        <Group justify="space-between">
          <Text size="xl" fw={700}>
            {formatCalendarHeader(currentDate, 'month')}
          </Text>
          
          {totalSlotsPerDay > 0 && (
            <Group gap="xs">
              <Text size="sm" c="dimmed">
                {totalSlotsPerDay} slots/day
              </Text>
            </Group>
          )}
        </Group>
      </Paper>

      {/* Calendar grid */}
      <Card p="md" withBorder>
        <Stack gap="xs">
          {/* Day names header */}
          <Grid gutter="xs">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
              <Grid.Col key={day} span={12 / 7}>
                <Text size="xs" fw={600} ta="center" tt="uppercase" c="dimmed">
                  {day}
                </Text>
              </Grid.Col>
            ))}
          </Grid>

          {/* Week rows */}
          {weeks.map((week, weekIndex) => (
            <Grid key={weekIndex} gutter="xs">
              {week.map((date) => {
                const stats = getDayStats(cellStatuses, assignments, date, totalSlotsPerDay);
                
                return (
                  <Grid.Col key={date.toISOString()} span={12 / 7}>
                    <DayCell
                      date={date}
                      currentMonth={currentDate}
                      stats={stats}
                      onDateClick={onDateClick} // FIXED: 10-10-2025 21:40:00 - Pass callback directly, not wrapped
                    />
                  </Grid.Col>
                );
              })}
            </Grid>
          ))}
        </Stack>
      </Card>

      {/* Legend */}
      <Paper p="sm" withBorder bg="gray.0">
        <Group gap="md" justify="center">
          <Group gap={4}>
            <div
              style={{
                width: 12,
                height: 12,
                backgroundColor: 'var(--mantine-color-green-6)',
                borderRadius: 2,
              }}
            />
            <Text size="xs">Low (0-25%)</Text>
          </Group>
          
          <Group gap={4}>
            <div
              style={{
                width: 12,
                height: 12,
                backgroundColor: 'var(--mantine-color-blue-6)',
                borderRadius: 2,
              }}
            />
            <Text size="xs">Moderate (25-50%)</Text>
          </Group>
          
          <Group gap={4}>
            <div
              style={{
                width: 12,
                height: 12,
                backgroundColor: 'var(--mantine-color-yellow-6)',
                borderRadius: 2,
              }}
            />
            <Text size="xs">High (50-70%)</Text>
          </Group>
          
          <Group gap={4}>
            <div
              style={{
                width: 12,
                height: 12,
                backgroundColor: 'var(--mantine-color-orange-6)',
                borderRadius: 2,
              }}
            />
            <Text size="xs">Very High (70-90%)</Text>
          </Group>
          
          <Group gap={4}>
            <div
              style={{
                width: 12,
                height: 12,
                backgroundColor: 'var(--mantine-color-red-6)',
                borderRadius: 2,
              }}
            />
            <Text size="xs">Critical (90-100%)</Text>
          </Group>
        </Group>
      </Paper>
    </Stack>
  );
};

export default MonthView;

